import React, { useState, useRef, useEffect } from 'react';
import { Card, SectionTitle } from './UI';

// ─── helpers ────────────────────────────────────────────────────────────────

function buildSystemPrompt() {
  return `Sei un analista finanziario senior specializzato in titoli della Borsa Italiana (FTSE MIB).
Rispondi sempre in italiano, con un tono professionale ma accessibile.
Usa dati concreti quando disponibili. Sii diretto e preciso.
Non dare mai consigli di investimento prescrittivi ("compra", "vendi"); formula sempre il tuo giudizio come analisi informativa.
Struttura le risposte in modo chiaro: usa paragrafi separati, non elenchi puntati eccessivi.
Chiudi sempre con una breve nota sui rischi principali.`;
}

function buildDataContext({ ticker, quoteData: q, technicalData: t, newsData: n, scores }) {
  const fmt = (v, d = 2) => v != null ? Number(v).toFixed(d) : 'N/D';
  const sig = t?.signals || {};
  const ind = t?.indicators || {};
  const lvl = t?.levels || {};
  const ns  = n?.sentiment_summary || {};

  return `
=== DATI TITOLO: ${ticker} ===
Società: ${q?.name || ticker}
Settore: ${q?.sector || 'N/D'} | Industria: ${q?.industry || 'N/D'}

--- QUOTAZIONE ---
Prezzo: €${fmt(q?.price, 3)} | Variazione: ${fmt(q?.change_pct)}%
52w High: €${fmt(q?.['52w_high'], 3)} | 52w Low: €${fmt(q?.['52w_low'], 3)}
Volume: ${q?.volume ? (q.volume / 1e6).toFixed(1) + 'M' : 'N/D'}

--- FONDAMENTALI ---
P/E trailing: ${fmt(q?.pe_ratio, 1)}x | P/E forward: ${fmt(q?.forward_pe, 1)}x
EV/EBITDA: ${fmt(q?.ev_ebitda, 1)}x | P/Book: ${fmt(q?.price_to_book, 2)}x
Dividend Yield: ${q?.dividend_yield ? q.dividend_yield + '%' : 'N/D'}
ROE: ${q?.roe ? q.roe + '%' : 'N/D'} | Beta: ${fmt(q?.beta, 2)}
Debt/Equity: ${q?.debt_to_equity ? (q.debt_to_equity / 100).toFixed(2) : 'N/D'}
Market Cap: €${q?.market_cap ? (q.market_cap / 1e9).toFixed(1) + 'B' : 'N/D'}
EPS: €${fmt(q?.eps, 3)} | Free Cash Flow: €${q?.free_cash_flow ? (q.free_cash_flow / 1e6).toFixed(0) + 'M' : 'N/D'}

--- ANALISI TECNICA ---
RSI(14): ${fmt(ind.rsi_14, 1)} → ${sig.rsi || 'N/D'}
MACD: ${fmt(ind.macd_line, 4)} / Signal: ${fmt(ind.macd_signal, 4)} → ${sig.macd || 'N/D'}
SMA20: €${fmt(ind.sma_20, 3)} → ${sig.sma_20 || 'N/D'}
SMA50: €${fmt(ind.sma_50, 3)} → ${sig.sma_50 || 'N/D'}
EMA200: €${fmt(ind.ema_200, 3)} → ${sig.ema_200 || 'N/D'}
ADX(14): ${fmt(ind.adx_14, 1)} → Trend ${sig.trend || 'N/D'}
Stocastico %K: ${fmt(ind.stoch_k, 1)} → ${sig.stochastic || 'N/D'}
BB Upper: €${fmt(ind.bb_upper, 3)} | BB Lower: €${fmt(ind.bb_lower, 3)}
ATR(14): €${fmt(ind.atr_14, 3)}
Resistenza 1: €${fmt(lvl.resistance_1, 3)} | Supporto 1: €${fmt(lvl.support_1, 3)}
Segnali BUY: ${t?.buy_signals ?? 'N/D'} | SELL: ${t?.sell_signals ?? 'N/D'} | NEUTRO: ${t?.neutral_signals ?? 'N/D'}

--- SENTIMENT NOTIZIE ---
Notizie analizzate: ${n?.total_news ?? 0}
Sentiment globale: ${ns.global || 'N/D'} | Score: ${ns.score ?? 'N/D'}/100
Positive: ${ns.positive ?? 0} | Negative: ${ns.negative ?? 0} | Neutre: ${ns.neutral ?? 0}
${n?.items?.slice(0, 3).map(i => `• [${i.sentiment}] ${i.title}`).join('\n') || 'Nessuna notizia disponibile'}

--- SCORE COMPOSITO ---
Tecnica (35%): ${scores.tech}/100
Fondamentale (35%): ${scores.fund}/100
Sentiment (20%): ${scores.news}/100
Macro (10%): ${scores.macro}/100
TOTALE: ${scores.composite}/100
`.trim();
}

// ─── streaming fetch ─────────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_URL || '/api';

async function streamAnalisi({ systemPrompt, messages, onChunk, onDone, onError, signal }) {
  try {
    const response = await fetch(`${API_BASE}/ai/chat`, {
      method: 'POST',
      signal,
      credentials: 'same-origin',   // porta il cookie di sessione
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ system: systemPrompt, messages }),
    });

    if (response.status === 401) {
      // Sessione scaduta: LoginGate ascolta e rimette la schermata di accesso.
      window.dispatchEvent(new CustomEvent('psa:sessione-scaduta'));
      throw new Error('Sessione non valida');
    }
    if (!response.ok) {
      const dettaglio = await response.json().catch(() => ({}));
      throw new Error(dettaglio.detail || `Errore ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const json = line.slice(6).trim();
        if (!json || json === '[DONE]') continue;
        try {
          const evt = JSON.parse(json);
          // L'errore può arrivare a metà flusso: lo streaming è già aperto e
          // non può più diventare un codice HTTP.
          if (evt.error) throw new Error(evt.error);
          if (evt.delta) onChunk(evt.delta);
        } catch (e) {
          if (e instanceof SyntaxError) continue;  // riga incompleta
          throw e;
        }
      }
    }
    onDone();
  } catch (e) {
    if (e.name !== 'AbortError') onError(e.message);
  }
}

// ─── Markdown-ish renderer (no external dep) ────────────────────────────────

function RenderText({ text }) {
  if (!text) return null;
  const lines = text.split('\n');
  return (
    <div style={{ lineHeight: 1.7 }}>
      {lines.map((line, i) => {
        if (line.startsWith('### ')) return <h4 key={i} style={{ fontSize: 13, fontWeight: 700, color: '#60a5fa', margin: '14px 0 4px', letterSpacing: '0.03em' }}>{line.slice(4)}</h4>;
        if (line.startsWith('## '))  return <h3 key={i} style={{ fontSize: 14, fontWeight: 700, color: '#93c5fd', margin: '16px 0 6px' }}>{line.slice(3)}</h3>;
        if (line.startsWith('# '))   return <h2 key={i} style={{ fontSize: 15, fontWeight: 700, color: '#bfdbfe', margin: '18px 0 8px' }}>{line.slice(2)}</h2>;
        if (line.startsWith('- ') || line.startsWith('• ')) {
          return <div key={i} style={{ display: 'flex', gap: 8, paddingLeft: 8, margin: '2px 0' }}>
            <span style={{ color: '#3b82f6', flexShrink: 0 }}>·</span>
            <span style={{ fontSize: 13, color: '#94a3b8' }}>{line.slice(2)}</span>
          </div>;
        }
        if (line.startsWith('**') && line.endsWith('**')) {
          return <p key={i} style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0', margin: '6px 0' }}>{line.slice(2, -2)}</p>;
        }
        if (line.trim() === '') return <div key={i} style={{ height: 8 }} />;
        // inline bold
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        return (
          <p key={i} style={{ fontSize: 13, color: '#94a3b8', margin: '2px 0' }}>
            {parts.map((p, j) =>
              p.startsWith('**') && p.endsWith('**')
                ? <strong key={j} style={{ color: '#cbd5e1', fontWeight: 600 }}>{p.slice(2, -2)}</strong>
                : p
            )}
          </p>
        );
      })}
    </div>
  );
}

// ─── Suggested questions ─────────────────────────────────────────────────────

const SUGGESTED = [
  'Fornisci una sintesi completa del titolo e il tuo giudizio',
  'Quali sono i principali catalizzatori nei prossimi 6 mesi?',
  'Confronta la valutazione attuale con i competitor europei',
  'Analizza il profilo di rischio/rendimento per un investitore retail',
  'Come interpretare i segnali tecnici in conflitto?',
  'Quali livelli di prezzo monitorare nelle prossime settimane?',
];

// ─── Main component ──────────────────────────────────────────────────────────

export default function ClaudeAnalysis({ ticker, quoteData, technicalData, newsData, scores }) {
  const [messages, setMessages]     = useState([]);
  const [input, setInput]           = useState('');
  const [streaming, setStreaming]    = useState(false);
  const [error, setError]           = useState(null);
  const [aiStatus, setAiStatus]     = useState(null);   // fornitore e se è configurato lato server
  const abortRef  = useRef(null);
  const bottomRef = useRef(null);

  // Auto-scroll to bottom on new content
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages]);

  // Reset conversation when ticker changes
  useEffect(() => { setMessages([]); setError(null); }, [ticker]);

  // Una chiave rimasta da quando il pannello parlava direttamente con
  // Anthropic dal browser: non serve più e non deve restare lì.
  useEffect(() => { localStorage.removeItem('anthropic_key'); }, []);

  // Il server dice se il verdetto AI è configurato, e con quale motore.
  useEffect(() => {
    fetch(`${API_BASE}/ai/status`, { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : null)
      .then(setAiStatus)
      .catch(() => setAiStatus(null));
  }, []);

  const send = async (userText) => {
    const text = (userText || input).trim();
    if (!text || streaming) return;

    setInput('');
    setError(null);

    const dataCtx   = buildDataContext({ ticker, quoteData, technicalData, newsData, scores });
    const systemPrm = buildSystemPrompt();

    const userMsg = {
      role: 'user',
      content: messages.length === 0
        ? `${dataCtx}\n\n---\n\nDomanda: ${text}`
        : text,
    };

    const newMessages = [...messages, { role: 'user', display: text }, { role: 'assistant', display: '' }];
    setMessages(newMessages);
    setStreaming(true);

    const apiMessages = [
      ...messages.filter(m => m.role !== undefined).map(m => ({ role: m.role, content: m.display || m.content })),
      userMsg,
    ].filter(m => m.role && m.content);

    abortRef.current = new AbortController();
    let fullText = '';

    await streamAnalisi({
      systemPrompt: systemPrm,
      messages: apiMessages,
      signal: abortRef.current.signal,
      onChunk: (chunk) => {
        fullText += chunk;
        setMessages(prev => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: 'assistant', display: fullText };
          return copy;
        });
      },
      onDone: () => setStreaming(false),
      onError: (msg) => { setError(msg); setStreaming(false); },
    });
  };

  const stop = () => { abortRef.current?.abort(); setStreaming(false); };
  const clear = () => { setMessages([]); setError(null); };

  const hasMessages = messages.length > 0;

  return (
    <Card style={{ marginTop: 20, borderColor: '#1e3a5f' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>✦</div>
          <SectionTitle style={{ marginBottom: 0 }}>Analisi Claude AI</SectionTitle>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {hasMessages && (
            <button onClick={clear} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, border: '1px solid #1e2736', background: 'transparent', color: '#475569', cursor: 'pointer' }}>
              Nuova chat
            </button>
          )}
          {aiStatus && (
            <span title={`Motore: ${aiStatus.model}`} style={{
              fontSize: 11, padding: '3px 10px', borderRadius: 6,
              background: aiStatus.configured ? 'rgba(16,185,129,0.1)' : 'rgba(148,163,184,0.1)',
              border: `1px solid ${aiStatus.configured ? 'rgba(16,185,129,0.3)' : 'rgba(148,163,184,0.25)'}`,
              color: aiStatus.configured ? '#10b981' : '#94a3b8',
            }}>
              {aiStatus.configured ? `● ${aiStatus.model}` : '○ non configurato'}
            </span>
          )}
        </div>
      </div>

      {aiStatus && !aiStatus.configured && (
        <div style={{ marginBottom: 16, padding: 14, background: '#0a0c10', borderRadius: 8, border: '1px solid #1e2736', fontSize: 12, color: '#94a3b8', lineHeight: 1.6 }}>
          Il verdetto AI non è ancora attivo: manca la chiave del fornitore
          (<code style={{ color: '#60a5fa' }}>{aiStatus.provider}</code>) fra le variabili
          d'ambiente del server. Si imposta una volta sola in Coolify, non su ogni dispositivo.
        </div>
      )}

      {/* Suggested questions (show only before first message) */}
      {!hasMessages && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: '#334155', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Domande suggerite</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {SUGGESTED.map((q, i) => (
              <button key={i} onClick={() => send(q)} style={{
                fontSize: 12, padding: '6px 12px', borderRadius: 20, cursor: 'pointer',
                background: '#0f1319', border: '1px solid #1e2736', color: '#64748b',
                textAlign: 'left', lineHeight: 1.3,
                transition: 'border-color 0.15s, color 0.15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#2563eb60'; e.currentTarget.style.color = '#94a3b8'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e2736'; e.currentTarget.style.color = '#64748b'; }}
              >{q}</button>
            ))}
          </div>
        </div>
      )}

      {/* Chat history */}
      {hasMessages && (
        <div style={{ maxHeight: 480, overflowY: 'auto', marginBottom: 14, paddingRight: 4 }}>
          {messages.map((msg, i) => (
            <div key={i} style={{
              marginBottom: 16,
              display: 'flex',
              flexDirection: 'column',
              alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}>
              <div style={{ fontSize: 10, color: '#334155', marginBottom: 4, letterSpacing: '0.05em' }}>
                {msg.role === 'user' ? 'Tu' : `Claude · ${ticker}`}
              </div>
              <div style={{
                maxWidth: '90%',
                padding: '10px 14px',
                borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '2px 12px 12px 12px',
                background: msg.role === 'user' ? '#1e3a5f' : '#0f1319',
                border: `1px solid ${msg.role === 'user' ? '#2563eb30' : '#1e2736'}`,
              }}>
                {msg.role === 'user'
                  ? <p style={{ fontSize: 13, color: '#bfdbfe', margin: 0 }}>{msg.display}</p>
                  : <RenderText text={msg.display} />}
                {msg.role === 'assistant' && streaming && i === messages.length - 1 && (
                  <span style={{ display: 'inline-block', width: 6, height: 14, background: '#3b82f6', borderRadius: 2, marginLeft: 2, animation: 'blink 1s step-end infinite', verticalAlign: 'middle' }} />
                )}
              </div>
            </div>
          ))}
          {error && (
            <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, color: '#f87171', fontSize: 12, marginBottom: 12 }}>
              ⚠ {error}
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      )}

      {/* Input area */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={`Fai una domanda su ${ticker}… (Invio per inviare, Shift+Invio per andare a capo)`}
          rows={2}
          style={{
            flex: 1, fontSize: 13, padding: '10px 14px',
            background: '#0a0c10', border: '1px solid #1e2736', borderRadius: 10,
            color: '#e2e8f0', outline: 'none', resize: 'none',
            fontFamily: "'Inter', sans-serif", lineHeight: 1.5,
          }}
          onFocus={e => e.target.style.borderColor = '#2563eb60'}
          onBlur={e => e.target.style.borderColor = '#1e2736'}
        />
        {streaming
          ? <button onClick={stop} style={{ padding: '10px 18px', borderRadius: 10, border: '1px solid #ef444440', background: 'rgba(239,68,68,0.1)', color: '#f87171', cursor: 'pointer', fontSize: 13, fontWeight: 600, flexShrink: 0 }}>■ Stop</button>
          : <button onClick={() => send()} disabled={!input.trim()} style={{
              padding: '10px 18px', borderRadius: 10, border: '1px solid #2563eb60',
              background: input.trim() ? '#1e3a5f' : 'transparent',
              color: input.trim() ? '#60a5fa' : '#334155',
              cursor: input.trim() ? 'pointer' : 'default', fontSize: 13, fontWeight: 600, flexShrink: 0,
            }}>Invia ↗</button>
        }
      </div>

      <style>{`@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }`}</style>
    </Card>
  );
}
